import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { orchard } from '../../theme';

/* Small looping pictures of what a lesson teaches — drawn in CSS, no
   screenshots (no children's data, light, always up to date). */

const pop = keyframes`
  0%, 8% { opacity: 0; transform: translateY(8px) scale(.94); }
  16%, 86% { opacity: 1; transform: none; }
  100% { opacity: 0; transform: none; }
`;
const slideDown = keyframes`
  0% { top: 8%; } 100% { top: 88%; }
`;
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: .9; }
  50% { transform: scale(1.12); opacity: 1; }
`;
const ring = keyframes`
  0% { transform: scale(.7); opacity: .7; } 100% { transform: scale(1.8); opacity: 0; }
`;
const bars = keyframes`
  0%, 100% { transform: scaleY(.3); } 50% { transform: scaleY(1); }
`;
const draw = keyframes`
  0% { stroke-dashoffset: 300; } 55%, 100% { stroke-dashoffset: 0; }
`;
const fill = keyframes`
  0% { width: 8%; } 60%, 100% { width: var(--w); }
`;
const blink = keyframes`
  0%, 49% { opacity: 1; } 50%, 100% { opacity: .2; }
`;
const travel = keyframes`
  0%, 15% { left: 4%; } 45%, 55% { left: 36%; } 85%, 100% { left: 68%; }
`;
const dots = keyframes`
  0%, 80%, 100% { opacity: .25; } 40% { opacity: 1; }
`;

const LOOP = 6; // seconds

function Stage({ children, tint, h = 150 }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const c = tint || orchard.cherry;
  return (
    <Box
      aria-hidden
      sx={{
        position: 'relative',
        height: h,
        color: 'text.primary',
        borderRadius: '18px',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${alpha(c, dark ? 0.22 : 0.1)}, ${alpha(orchard.honey, dark ? 0.16 : 0.12)})`,
        border: '1px solid',
        borderColor: alpha(c, 0.18),
        '& *': {
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none !important',
          },
        },
      }}
    >
      {children}
    </Box>
  );
}

// a white rounded card inside a stage
function Card({ sx, children, delay = 0, anim = true }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        bgcolor: 'background.paper',
        borderRadius: '12px',
        boxShadow: '0 6px 18px -10px rgba(0,0,0,.35)',
        px: 1.25,
        py: 0.75,
        fontSize: '0.78rem',
        fontWeight: 700,
        color: 'text.primary',
        animation: anim
          ? `${pop} ${LOOP}s ${delay}s ease infinite both`
          : 'none',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const Line = ({ w = '70%', o = 0.18, h = 7, sx }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: 4,
      bgcolor: (t) => alpha(t.palette.text.primary, o),
      ...sx,
    }}
  />
);

function Day() {
  const rows = [
    ['09:00', 'English · Colours', orchard.cherry],
    ['09:50', 'Maths · KA 2-5', orchard.honey],
    ['10:40', 'Music · 🎵 Набор', '#2E7D5B'],
  ];
  return (
    <Stage>
      <Box
        sx={{
          position: 'absolute',
          inset: '10px 14px',
          display: 'grid',
          gap: 1,
        }}
      >
        {rows.map(([t, n, c], i) => (
          <Box
            key={t}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'background.paper',
              borderRadius: '10px',
              px: 1,
              py: 0.6,
              borderLeft: `4px solid ${c}`,
              animation: `${pop} ${LOOP}s ${i * 0.25}s ease infinite both`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 800,
                color: 'text.secondary',
              }}
            >
              {t}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
              {n}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: 8,
          right: 8,
          height: 2,
          bgcolor: orchard.cherry,
          animation: `${slideDown} ${LOOP}s linear infinite alternate`,
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -2,
            top: -4,
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: orchard.cherry,
          },
        }}
      />
    </Stage>
  );
}

function Calendar() {
  const marks = {
    3: orchard.honey,
    8: orchard.cherry,
    11: '#2E7D5B',
    16: orchard.cherry,
    19: orchard.honey,
  };
  return (
    <Stage tint={orchard.honey}>
      <Box
        sx={{
          position: 'absolute',
          inset: '12px 16px',
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.6,
        }}
      >
        {Array.from({ length: 21 }, (_, i) => (
          <Box
            key={i}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '7px',
              position: 'relative',
              fontSize: '0.6rem',
              fontWeight: 800,
              p: '2px 4px',
              color: 'text.secondary',
            }}
          >
            {i + 1}
            {marks[i] && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 4,
                  bottom: 4,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: marks[i],
                  animation: `${pop} ${LOOP}s ${(i % 5) * 0.3}s ease infinite both`,
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Stage>
  );
}

function Tasks() {
  return (
    <Stage tint="#7A4988">
      {['💭 Думаю', '⚡ Делаю', '✅ Готово'].map((c, i) => (
        <Box
          key={c}
          sx={{
            position: 'absolute',
            top: 10,
            bottom: 10,
            left: `${4 + i * 32}%`,
            width: '28%',
            borderRadius: '10px',
            bgcolor: (t) => alpha(t.palette.background.paper, 0.6),
            fontSize: '0.66rem',
            fontWeight: 800,
            textAlign: 'center',
            pt: 0.5,
          }}
        >
          {c}
        </Box>
      ))}
      <Card
        anim={false}
        sx={{
          top: 44,
          width: '26%',
          animation: `${travel} ${LOOP}s ease-in-out infinite`,
          fontSize: '0.68rem',
        }}
      >
        Отчёт за неделю
      </Card>
    </Stage>
  );
}

function Chat({
  q = 'Сделай рабочий лист про фигуры',
  a = 'Готово! Лист A4 с фигурами 👇',
  card = '📄 Shapes · PDF',
}) {
  return (
    <Stage>
      <Card
        sx={{
          top: 10,
          right: 12,
          bgcolor: orchard.cherry,
          color: '#fff',
          maxWidth: '72%',
        }}
      >
        {q}
      </Card>
      <Card delay={0.9} sx={{ top: 50, left: 12, maxWidth: '72%' }}>
        🍒 {a}
      </Card>
      <Card
        delay={1.5}
        sx={{ top: 92, left: 12, border: `1px dashed ${orchard.cherry}` }}
      >
        {card}
      </Card>
    </Stage>
  );
}

function Voice() {
  return (
    <Stage tint={orchard.cherry}>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
        }}
      >
        {[0, 1].map((i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `2px solid ${orchard.cherry}`,
              animation: `${ring} 2s ${i}s ease-out infinite`,
            }}
          />
        ))}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: orchard.cherry,
            display: 'grid',
            placeItems: 'center',
            fontSize: 28,
            animation: `${pulse} 1.6s ease-in-out infinite`,
          }}
        >
          🎙️
        </Box>
      </Box>
      <Equalizer sx={{ position: 'absolute', right: 18, bottom: 18 }} />
    </Stage>
  );
}

function Equalizer({ sx, n = 7 }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '3px',
        height: 26,
        ...sx,
      }}
    >
      {Array.from({ length: n }, (_, i) => (
        <Box
          key={i}
          sx={{
            width: 4,
            height: '100%',
            borderRadius: 2,
            bgcolor: i % 2 ? orchard.honey : orchard.cherry,
            transformOrigin: 'bottom',
            animation: `${bars} ${0.6 + (i % 4) * 0.15}s ${i * 0.08}s ease-in-out infinite`,
          }}
        />
      ))}
    </Box>
  );
}

function Phone() {
  return (
    <Stage tint="#229ED9">
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: 8,
          bottom: -20,
          width: 150,
          ml: '-75px',
          borderRadius: '20px',
          border: '3px solid',
          borderColor: (t) => alpha(t.palette.text.primary, 0.25),
          bgcolor: (t) =>
            alpha('#229ED9', t.palette.mode === 'dark' ? 0.18 : 0.08),
        }}
      >
        <Card
          sx={{
            top: 12,
            right: 8,
            bgcolor: '#229ED9',
            color: '#fff',
            fontSize: '0.66rem',
          }}
        >
          🎤 0:07
        </Card>
        <Card delay={1} sx={{ top: 46, left: 8, fontSize: '0.66rem' }}>
          🍒 Сделала! 🎵
        </Card>
        <Card
          delay={1.6}
          sx={{
            top: 80,
            left: 8,
            fontSize: '0.62rem',
            border: '1px solid #229ED9',
          }}
        >
          ▶ Песня про осень
        </Card>
      </Box>
    </Stage>
  );
}

function Memory() {
  const notes = [
    '🤒 Ева болела 12–16 окт',
    '🙈 Тимур стесняется',
    '⭐ Лиза любит рисовать',
  ];
  return (
    <Stage tint={orchard.honey}>
      {notes.map((n, i) => (
        <Card
          key={n}
          delay={i * 0.6}
          sx={{
            top: 12 + i * 40,
            left: 14 + i * 18,
            transform: `rotate(${i - 1}deg)`,
          }}
        >
          {n}
        </Card>
      ))}
    </Stage>
  );
}

const WORDS = ['Circle,', 'circle,', 'round', 'and', 'round'];
function Karaoke() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % (WORDS.length + 3)), 520);
    return () => clearInterval(t);
  }, []);
  return (
    <Stage h={150}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: '0.8rem', color: 'text.disabled', fontWeight: 700 }}
          >
            Shapes are all around
          </Typography>
          <Typography
            sx={{
              fontFamily: orchard.fontDisplay,
              fontWeight: 700,
              fontSize: '1.45rem',
              lineHeight: 1.3,
            }}
          >
            {WORDS.map((w, k) => (
              <Box
                key={k}
                component="span"
                sx={{
                  color: k < i ? orchard.cherry : 'text.primary',
                  transition: 'color .25s',
                }}
              >
                {w}{' '}
              </Box>
            ))}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: 'text.secondary',
              fontWeight: 700,
            }}
          >
            Square has four sides
          </Typography>
        </Box>
      </Box>
    </Stage>
  );
}

function Song() {
  return (
    <Stage>
      <Card
        anim={false}
        sx={{
          top: 14,
          left: 14,
          right: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '9px',
            bgcolor: orchard.cherry,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
          }}
        >
          ▶
        </Box>
        <Box sx={{ flex: 1 }}>
          Осенняя песенка
          <Line w="60%" sx={{ mt: 0.5 }} />
        </Box>
      </Card>
      <Box
        sx={{
          position: 'absolute',
          left: 16,
          right: 16,
          bottom: 18,
          display: 'flex',
          alignItems: 'flex-end',
          gap: '3px',
          height: 44,
        }}
      >
        {Array.from({ length: 34 }, (_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: `${30 + ((i * 37) % 70)}%`,
              borderRadius: 2,
              bgcolor:
                i < 14
                  ? orchard.cherry
                  : (t) => alpha(t.palette.text.primary, 0.2),
              transformOrigin: 'bottom',
              animation: `${bars} ${1 + (i % 5) * 0.2}s ${i * 0.04}s ease-in-out infinite`,
            }}
          />
        ))}
      </Box>
    </Stage>
  );
}

function Sheet() {
  const stroke = {
    fill: 'none',
    strokeWidth: 3,
    strokeDasharray: '6 5',
    strokeLinecap: 'round',
  };
  return (
    <Stage tint={orchard.honey}>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: 10,
          bottom: 10,
          width: 104,
          ml: '-52px',
          bgcolor: '#fff',
          borderRadius: '6px',
          boxShadow: '0 8px 20px -10px rgba(0,0,0,.4)',
          p: 1,
        }}
      >
        <Line w="70%" o={0.3} h={6} sx={{ bgcolor: '#999' }} />
        <svg viewBox="0 0 100 90" width="100%" height="100">
          {[
            <circle
              key="c"
              cx="25"
              cy="28"
              r="16"
              stroke={orchard.cherry}
              style={stroke}
            />,
            <rect
              key="s"
              x="58"
              y="12"
              width="30"
              height="30"
              stroke="#2E7D5B"
              style={stroke}
            />,
            <polygon
              key="t"
              points="25,52 43,84 7,84"
              stroke={orchard.honey}
              style={stroke}
            />,
            <polygon
              key="st"
              points="73,50 78,64 92,64 81,72 85,86 73,78 61,86 65,72 54,64 68,64"
              stroke="#7A4988"
              style={stroke}
            />,
          ].map((el, i) =>
            React.cloneElement(el, {
              style: {
                ...stroke,
                strokeDasharray: 300,
                animation: `${draw} ${LOOP}s ${i * 0.4}s ease infinite`,
              },
            })
          )}
        </svg>
      </Box>
    </Stage>
  );
}

function Tiles({ items, tint }) {
  return (
    <Stage tint={tint}>
      <Box
        sx={{
          position: 'absolute',
          inset: '14px 24px',
          display: 'grid',
          gridTemplateColumns: `repeat(${items.length > 4 ? 3 : 4}, 1fr)`,
          gap: 1,
        }}
      >
        {items.map((e, i) => (
          <Box
            key={i}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '12px',
              display: 'grid',
              placeItems: 'center',
              fontSize: 30,
              boxShadow: '0 6px 16px -10px rgba(0,0,0,.35)',
              animation: `${pop} ${LOOP}s ${i * 0.2}s ease infinite both`,
            }}
          >
            {e}
          </Box>
        ))}
      </Box>
    </Stage>
  );
}

function Kit() {
  return (
    <Stage>
      <Card
        anim={false}
        sx={{
          top: 10,
          left: 14,
          right: 14,
          bottom: 10,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.85),
        }}
      >
        📦 Осень · среда 10:00 English
        {[
          '🎵 Осенняя песенка · караоке',
          '📜 Листья жёлтые летят…',
          '📄 Раскраска «Листья» · PDF',
        ].map((x, i) => (
          <Box
            key={x}
            sx={{
              mt: 0.75,
              px: 1,
              py: 0.4,
              borderRadius: '8px',
              bgcolor: (t) =>
                alpha(orchard.cherry, t.palette.mode === 'dark' ? 0.2 : 0.07),
              fontSize: '0.72rem',
              animation: `${pop} ${LOOP}s ${0.3 + i * 0.5}s ease infinite both`,
            }}
          >
            {x}
          </Box>
        ))}
      </Card>
    </Stage>
  );
}

function Doc() {
  return (
    <Stage tint="#7A4988">
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: 10,
          bottom: 10,
          width: 120,
          ml: '-60px',
          bgcolor: '#fff',
          borderRadius: '6px',
          p: 1.25,
          boxShadow: '0 8px 20px -10px rgba(0,0,0,.4)',
        }}
      >
        <Box
          sx={{
            height: 8,
            width: '55%',
            borderRadius: 4,
            bgcolor: orchard.cherry,
            mb: 1,
          }}
        />
        {[90, 75, 85, 60, 80].map((w, i) => (
          <Box
            key={i}
            sx={{
              height: 5,
              width: `${w}%`,
              borderRadius: 3,
              bgcolor: '#ccc',
              mb: 0.75,
              animation: `${pop} ${LOOP}s ${i * 0.25}s ease infinite both`,
            }}
          />
        ))}
      </Box>
      <Card
        delay={1.4}
        sx={{ right: 18, bottom: 16, bgcolor: orchard.cherry, color: '#fff' }}
      >
        PDF ✓
      </Card>
    </Stage>
  );
}

function Assess() {
  const rows = [
    ['Colours', '85%'],
    ['Numbers 1–10', '60%'],
    ['Shapes', '72%'],
  ];
  return (
    <Stage tint="#2E7D5B">
      <Box
        sx={{
          position: 'absolute',
          inset: '16px 20px',
          display: 'grid',
          gap: 1.25,
        }}
      >
        {rows.map(([n, w], i) => (
          <Box key={n}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800 }}>
              {n}
            </Typography>
            <Box
              sx={{
                height: 9,
                borderRadius: 5,
                bgcolor: (t) => alpha(t.palette.text.primary, 0.1),
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  '--w': w,
                  height: '100%',
                  borderRadius: 5,
                  bgcolor: '#2E7D5B',
                  animation: `${fill} ${LOOP}s ${i * 0.3}s ease infinite both`,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Stage>
  );
}

function Video() {
  return (
    <Stage tint={orchard.cherry}>
      <Box
        sx={{
          position: 'absolute',
          inset: '14px 30px',
          borderRadius: '12px',
          bgcolor: (t) => alpha(t.palette.text.primary, 0.12),
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            fontSize: 34,
          }}
        >
          🔺🟦🟡
        </Box>
        <Box
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            width: 38,
            height: 38,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            display: 'grid',
            placeItems: 'center',
            fontSize: 22,
            border: `2px solid ${orchard.cherry}`,
          }}
        >
          🙂
        </Box>
        <Box
          sx={{
            position: 'absolute',
            left: 8,
            top: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.7rem',
            fontWeight: 800,
          }}
        >
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              bgcolor: '#e53935',
              animation: `${blink} 1s infinite`,
            }}
          />
          REC 0:12
        </Box>
      </Box>
    </Stage>
  );
}

function Report() {
  return (
    <Stage tint="#229ED9">
      <Card sx={{ top: 12, left: 14, right: 40 }}>
        📝 Домашнее задание · Daily
        <Line w="90%" sx={{ mt: 0.75 }} />
        <Line w="70%" sx={{ mt: 0.5 }} />
      </Card>
      <Card
        delay={1.2}
        sx={{ bottom: 14, right: 14, bgcolor: '#229ED9', color: '#fff' }}
      >
        ✈️ Отправлено родителям
      </Card>
    </Stage>
  );
}

function Inbox() {
  return (
    <Stage tint={orchard.honey}>
      <Card
        anim={false}
        sx={{
          top: 18,
          left: 18,
          animation: `${travel} ${LOOP}s ease-in-out infinite`,
        }}
      >
        📨 3 файла
      </Card>
      <Card delay={2} sx={{ bottom: 16, right: 16 }}>
        📥 Входящие · новое
      </Card>
    </Stage>
  );
}

function Typing() {
  return (
    <Box sx={{ display: 'inline-flex', gap: '3px' }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            animation: `${dots} 1.2s ${i * 0.2}s infinite`,
          }}
        />
      ))}
    </Box>
  );
}

const DEMOS = {
  day: Day,
  calendar: Calendar,
  tasks: Tasks,
  chat: () => (
    <Chat
      q="План урока «Цвета» на 30 минут"
      a="Вот план: разминка, песня, игра…"
      card="📋 План урока · 30 мин"
    />
  ),
  voice: Voice,
  phone: Phone,
  memory: Memory,
  karaoke: Karaoke,
  song: Song,
  sheet: Sheet,
  pictures: () => (
    <Tiles items={['🍎', '🍌', '🍒', '🍋', '🍐', '🫐']} tint={orchard.honey} />
  ),
  stickers: () => <Tiles items={['😄', '🤩', '😎', '🥳']} tint="#7A4988" />,
  kit: Kit,
  doc: Doc,
  assess: Assess,
  video: Video,
  report: Report,
  inbox: Inbox,
};

export default function Demo({ kind }) {
  const D = DEMOS[kind] || Day;
  return <D />;
}

export { Stage, Equalizer, Typing, Chat };
